// Story 1.19 — TanStack mutation for POST /api/v1/account/delete-request.
//
// Per-submit `crypto.randomUUID()` idempotency key (NOT per modal-open;
// each submit gets its own key so a retry after a 422 ineligibility
// error has a fresh key when the user re-submits with the ineligibility
// resolved — same pattern as Stories 1.16/1.17/1.18).
//
// On success: invalidates account-deletion-status (so the page rerenders
// the PendingDeletionPanel) + wallet-balance (the user has just locked
// themselves out of any further wallet activity).
//
// On 422 ineligibility: extracts the full money snapshot (balance,
// escrow + the named offers holding it, live listings
// and refunds) so the IneligibilityChecklist can render a complete
// "items to resolve" picture without a second round-trip.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type RequestDeletionBody = {
  confirmation_text: string;
};

export type RequestDeletionResponse = {
  deletion_request_id: string;
  scheduled_for: string;
  cancellation_window_hours: number;
};

/** One live marketplace offer whose hold is blocking account closure. */
export type BlockingOffer = {
  offer_id: string;
  listing_id: string;
  kind: "share" | "sealed_dossier";
  amount_cents: number;
};

export type DeletionError = {
  code: string;
  message: string;
  field?: string;
  // 422 ACCOUNT_DELETION_INELIGIBLE carries reason + the full snapshot
  // so the SPA renders the checklist without a second round-trip.
  reason?:
    | "non_zero_balance"
    | "funds_held_in_escrow"
    | "active_marketplace_listings"
    // Story 8.11 — the EARNED pot. A creator with unspent earnings is refused
    // here and deferred by the worker, because after erasure neither exit (a
    // payout request or the free conversion) has a surface left to reach.
    | "non_zero_earnings"
    | "in_flight_refunds";
  balance_cents?: number;
  earnings_cents?: number;
  escrow_cents?: number;
  in_flight_refunds?: number;
  active_listings?: number;
  active_offers?: number;
  blocking_offers?: BlockingOffer[];
  // 409 ACCOUNT_DELETION_ALREADY_PENDING carries scheduled_for so the
  // SPA can route the user to the cancellation surface.
  scheduled_for?: string;
};

async function postDeletionRequest(
  body: RequestDeletionBody,
  idempotencyKey: string,
): Promise<RequestDeletionResponse> {
  const response = await fetch("/api/v1/account/delete-request", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotencyKey,
    },
    body: JSON.stringify(body),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: DeletionError = {
      code: "DELETION_REQUEST_FAILED",
      message: `Deletion request failed (HTTP ${response.status})`,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = { ...raw.error };
      }
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
  const envelope = (await response.json()) as { data: RequestDeletionResponse };
  return envelope.data;
}

export function useRequestDeletion() {
  const queryClient = useQueryClient();
  return useMutation<
    RequestDeletionResponse,
    DeletionError,
    { body: RequestDeletionBody; idempotencyKey: string }
  >({
    mutationFn: ({ body, idempotencyKey }) =>
      postDeletionRequest(body, idempotencyKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-deletion-status"] });
      queryClient.invalidateQueries({ queryKey: ["wallet-balance"] });
    },
  });
}
